/**
 * Centralized logging utility for debugging and tracking application flow
 * Provides different log levels and context-aware logging
 */

// Enable/disable logging globally
const LOGGING_ENABLED = true;
// Set minimum log level (0=debug, 1=info, 2=warn, 3=error)
const MIN_LOG_LEVEL = 0;

// Log levels
const LOG_LEVELS = {
  DEBUG: 0,
  INFO: 1,
  WARN: 2,
  ERROR: 3
};

// Log contexts for grouping related logs
export const LOG_CONTEXTS = {
  SUPABASE: 'SUPABASE',
  CHANNELS: 'CHANNELS',
  IMPORT: 'IMPORT',
  RENDER: 'RENDER',
  QUERY: 'QUERY',
  AUTH: 'AUTH',
  NETWORK: 'NETWORK',
  ACTIVITY: 'ACTIVITY'
};

// Unique request ID to track related operations
let requestCounter = 0;
const getRequestId = () => {
  requestCounter++;
  return `req_${Date.now()}_${requestCounter}`;
};

// Main logging function
function log(level: number, context: string, message: string, data?: any, requestId?: string) {
  if (!LOGGING_ENABLED || level < MIN_LOG_LEVEL) return;

  const timestamp = new Date().toISOString();
  const reqId = requestId || '';
  const prefix = `[${timestamp}][${context}]${reqId ? `[${reqId}]` : ''}`;
  
  switch (level) {
    case LOG_LEVELS.DEBUG:
      console.debug(`${prefix} 🔍 ${message}`, data || '');
      break;
    case LOG_LEVELS.INFO:
      console.info(`${prefix} ℹ️ ${message}`, data || '');
      break;
    case LOG_LEVELS.WARN:
      console.warn(`${prefix} ⚠️ ${message}`, data || '');
      break;
    case LOG_LEVELS.ERROR:
      console.error(`${prefix} 🔴 ${message}`, data || '');
      break;
  }
}

// Exported logging functions
export const logger = {
  debug: (context: string, message: string, data?: any, requestId?: string) => 
    log(LOG_LEVELS.DEBUG, context, message, data, requestId),
  
  info: (context: string, message: string, data?: any, requestId?: string) => 
    log(LOG_LEVELS.INFO, context, message, data, requestId),
  
  warn: (context: string, message: string, data?: any, requestId?: string) => 
    log(LOG_LEVELS.WARN, context, message, data, requestId),
  
  error: (context: string, message: string, data?: any, requestId?: string) => 
    log(LOG_LEVELS.ERROR, context, message, data, requestId),
  
  // Create a request ID to track related operations
  createRequestId: () => getRequestId(),
  
  // Track component renders
  trackRender: (componentName: string) => {
    log(LOG_LEVELS.DEBUG, LOG_CONTEXTS.RENDER, `Component rendered: ${componentName}`);
  },
  
  // Track query executions
  trackQuery: (queryKey: unknown[], message: string, data?: any) => {
    log(LOG_LEVELS.DEBUG, LOG_CONTEXTS.QUERY, 
      `${message} - Query key: ${JSON.stringify(queryKey)}`, data);
  },
  
  // Track Supabase operations
  trackSupabase: (operation: string, details: any, requestId?: string) => {
    log(LOG_LEVELS.DEBUG, LOG_CONTEXTS.SUPABASE, 
      `Supabase operation: ${operation}`, details, requestId);
  }
}; 